import { Calendar, Search, X, SlidersHorizontal } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  color?: string;
  count?: number;
}

export interface CustomFilter {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
  active: boolean;
  onClick: () => void;
}

export interface FilterBarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilters: FilterOption[];
  activeStatuses: string[];
  onStatusToggle: (status: string) => void;
  showDateFilter?: boolean;
  dateFrom?: string;
  dateTo?: string;
  onDateFromChange?: (date: string) => void;
  onDateToChange?: (date: string) => void;
  onClearFilters: () => void;
  totalItems: number;
  filteredItems: number;
  customFilters?: CustomFilter[];
}

/**
 * FilterBar Component v2 - FacturXPro Design System
 *
 * Barre de filtres avec glassmorphism et style Teal/Volvo
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  statusFilters,
  activeStatuses,
  onStatusToggle,
  showDateFilter = false,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClearFilters,
  totalItems,
  filteredItems,
  customFilters = [],
}) => {
  const { t } = useTranslation();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const resolvedSearchPlaceholder = searchPlaceholder || t('common.ui.filterBar.searchPlaceholder');

  const activeCustomFiltersCount = customFilters.filter(f => f.active).length;
  const activeFiltersCount =
    activeStatuses.length + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (searchValue ? 1 : 0) + activeCustomFiltersCount;

  const hasActiveFilters = activeFiltersCount > 0;

  return (
    <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 overflow-hidden hover:shadow-2xl transition-all duration-300">
      {/* Main filter row */}
      <div className="p-4 space-y-4">
        {/* Search bar and actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={resolvedSearchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
            />
            {searchValue && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 ${
              showAdvancedFilters || hasActiveFilters
                ? 'bg-teal-50 text-teal-700 border-2 border-teal-300 shadow-sm'
                : 'bg-white/30 text-gray-700 border-2 border-transparent hover:bg-white/50 backdrop-blur-sm'
            }`}
          >
            <SlidersHorizontal className="w-5 h-5" />
            <span className="hidden sm:inline">{t('common.ui.filterBar.filters')}</span>
            {activeFiltersCount > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-teal-600 rounded-full shadow-sm">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/30 text-gray-700 rounded-xl font-medium hover:bg-white/50 backdrop-blur-sm transition-all duration-300 border-2 border-white/40"
            >
              <X className="w-5 h-5" />
              <span className="hidden sm:inline">{t('common.ui.filterBar.clear')}</span>
            </button>
          )}
        </div>

        {/* Status filters chips - Always visible */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 mr-2">{t('common.ui.filterBar.status')}</span>
          {statusFilters.map((filter) => {
            const isActive = activeStatuses.includes(filter.value);
            const IconComponent = filter.icon;
            return (
              <button
                key={filter.value}
                onClick={() => onStatusToggle(filter.value)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? `bg-${filter.color}-100 text-${filter.color}-700 border-2 border-${filter.color}-300 shadow-sm`
                    : 'bg-white/30 text-gray-600 border-2 border-transparent hover:bg-white/50 backdrop-blur-sm'
                }`}
                style={
                  isActive && filter.color
                    ? {
                        backgroundColor: `var(--${filter.color}-100)`,
                        color: `var(--${filter.color}-700)`,
                        borderColor: `var(--${filter.color}-300)`,
                      }
                    : undefined
                }
              >
                {IconComponent && <IconComponent className="w-4 h-4" />}
                <span>{filter.label}</span>
                {filter.count !== undefined && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-white/50 rounded">
                    {filter.count}
                  </span>
                )}
              </button>
            );
          })}

          {/* Separator if custom filters exist */}
          {customFilters.length > 0 && (
            <span className="text-gray-300 mx-2">|</span>
          )}

          {/* Custom filters */}
          {customFilters.map((filter) => {
            const IconComponent = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={filter.onClick}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  filter.active
                    ? 'bg-teal-100 text-teal-700 border-2 border-teal-300 shadow-sm'
                    : 'bg-white/30 text-gray-600 border-2 border-transparent hover:bg-white/50 backdrop-blur-sm'
                }`}
              >
                {IconComponent && <IconComponent className="w-4 h-4" />}
                <span>{filter.label}</span>
                {filter.count !== undefined && filter.count > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded ${
                    filter.active ? 'bg-teal-200 text-teal-800' : 'bg-white/50'
                  }`}>
                    {filter.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Advanced filters (date range) */}
        {showAdvancedFilters && showDateFilter && (
          <div className="pt-4 border-t border-white/20">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('common.ui.filterBar.dateFrom')}
                </label>
                <input
                  type="date"
                  value={dateFrom || ''}
                  onChange={(e) => onDateFromChange?.(e.target.value)}
                  className="block w-full px-3 py-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('common.ui.filterBar.dateTo')}
                </label>
                <input
                  type="date"
                  value={dateTo || ''}
                  onChange={(e) => onDateToChange?.(e.target.value)}
                  className="block w-full px-3 py-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                />
              </div>
            </div>
          </div>
        )}

        {/* Results summary */}
        <div className="flex items-center justify-between text-sm text-gray-600 pt-2 border-t border-white/20">
          <span>
            {filteredItems === totalItems ? (
              <strong>{totalItems}</strong>
            ) : (
              <>
                <strong>{filteredItems}</strong> {t('common.ui.filterBar.onOf')} <strong>{totalItems}</strong>
              </>
            )}{' '}
            {filteredItems > 1 ? t('common.ui.filterBar.resultsCountPlural') : t('common.ui.filterBar.resultsCount')}
          </span>
          {hasActiveFilters && (
            <span className="text-teal-600 font-medium">
              {activeFiltersCount} {activeFiltersCount > 1 ? t('common.ui.filterBar.activeFilterPlural') : t('common.ui.filterBar.activeFilter')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
